import { Currency } from 'common/contracts';
import Decimal from 'decimal.js';

export interface FormatSettings {
  currency?: 'USD' | 'EUR';
  ratio?: number;
}

export const capitalizeWords = (str: string) => {
  return str.replace(/\b\w/g, (c) => c.toUpperCase());
};

export const capitalizeFirstLetter = (str: string) => {
  return str.charAt(0).toUpperCase() + str.slice(1);
};

export const transformQuantityValue = (inputValue: string) => {
  let value = inputValue;
  if (inputValue.startsWith('0') && inputValue !== '0') {
    value = inputValue.replace(/^0+/, '');
  }
  const parsedNumber = parseInt(value);
  if (!isNaN(parsedNumber) && String(parsedNumber) === value) {
    return parsedNumber;
  }
  try {
    return BigInt(inputValue).toString();
  } catch (error) {
    return 0;
  }
};

export const serializeDate = (
  date: Date | null | undefined,
  endOfDay: boolean = false,
  isUTC: boolean = false
) => {
  if (!date) return '';
  let { year, month, day, hours, minutes, seconds, milliseconds } =
    getDateBreakdown(date, isUTC);

  if (month < 10) {
    month = `0${month}`;
  }
  if (day < 10) {
    day = `0${day}`;
  }

  if (hours < 10) {
    hours = `0${hours}`;
  }
  if (minutes < 10) {
    minutes = `0${minutes}`;
  }
  if (seconds < 10) {
    seconds = `0${seconds}`;
  }
  if (milliseconds < 10) {
    milliseconds = `00${milliseconds}`;
  } else if (milliseconds < 100) {
    milliseconds = `0${milliseconds}`;
  }

  if (endOfDay) {
    hours = 23;
    minutes = 59;
    seconds = 59;
    milliseconds = 999;
  }

  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${milliseconds}Z`;
};

function getDateBreakdown(
  date: Date,
  isUTC: boolean = false
): Record<string, any> {
  if (isUTC) {
    return {
      year: date.getUTCFullYear(),
      month: date.getUTCMonth() + 1,
      day: date.getUTCDate(),
      hours: date.getUTCHours(),
      minutes: date.getUTCMinutes(),
      seconds: date.getUTCSeconds(),
      milliseconds: date.getUTCMilliseconds()
    };
  }
  return {
    year: date.getFullYear(),
    month: date.getMonth() + 1,
    day: date.getDate(),
    hours: date.getHours(),
    minutes: date.getMinutes(),
    seconds: date.getSeconds(),
    milliseconds: date.getMilliseconds()
  };
}

export const formatReceiptNumber = (receiptNumber: string): string => {
  if (!/^\d{9}$/.test(receiptNumber)) {
    return receiptNumber;
  }
  return receiptNumber.replace(/(\d{3})(?=\d)/g, '$1-');
};

export const getFormattedNumberWithCurrency = (
  value: number | Decimal | undefined,
  currency: Currency | undefined
) => {
  if (value === undefined) {
    return 'N/A';
  }
  const multiplier = currency?.multiplier || 100;
  const currencySymbol = currency?.currencySymbol;
  const fractionDigits = Math.log10(multiplier);
  const amount = `${currencySymbol}${new Decimal(value)
    .div(multiplier)
    .toFixed(fractionDigits)}`;

  return amount;
};

export const formatDate = (dateString: string) => {
  const date = new Date(dateString);

  const day = String(date.getUTCDate()).padStart(2, '0');
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const year = date.getUTCFullYear();
  let hours = date.getUTCHours();
  const minutes = String(date.getUTCMinutes()).padStart(2, '0');

  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12;
  hours = hours ? hours : 12;

  return `${day}/${month}/${year} ${String(hours).padStart(2, '0')}:${minutes} ${ampm}`;
};

export const formatDateShort = (dateString: string) => {
  const date = new Date(dateString);

  const month = date.toLocaleDateString('en-US', { month: 'short' });
  const day = date.getUTCDate();
  let hours = date.getUTCHours();
  const minutes = String(date.getUTCMinutes()).padStart(2, '0');

  const ampm = hours >= 12 ? 'pm' : 'am';
  hours = hours % 12;
  hours = hours ? hours : 12;

  return `${month} ${day} at ${hours}:${minutes} ${ampm}`;
};

// Utility function to format the duration
export const getDurationTextFromMinutes = (totalMinutes: number): string => {
  const days = Math.floor(totalMinutes / (60 * 24));
  const remainingMinutesAfterDays = totalMinutes % (60 * 24);
  const hours = Math.floor(remainingMinutesAfterDays / 60);
  const minutes = remainingMinutesAfterDays % 60;

  let durationParts: string[] = [];

  if (days > 0) {
    durationParts.push(`${days} day${days !== 1 ? 's' : ''}`);
  }

  if (hours > 0) {
    durationParts.push(`${hours} hour${hours !== 1 ? 's' : ''}`);
  }

  if (minutes > 0) {
    durationParts.push(`${minutes} minute${minutes !== 1 ? 's' : ''}`);
  }

  // Join the duration parts with "and" if there's more than one part
  return durationParts.length > 1
    ? durationParts.slice(0, -1).join(', ') + ' and ' + durationParts.slice(-1)
    : durationParts[0] || '';
};

export const truncateText = (text: string, maxLength: number = 30): string => {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};

/**
 * Helper function to detect if user is in US based on timezone
 */
const isUSTimezone = (): boolean => {
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  // List of US timezones
  const usTimeZones = [
    'America/New_York',
    'America/Chicago',
    'America/Denver',
    'America/Los_Angeles',
    'America/Phoenix',
    'America/Detroit',
    'America/Anchorage',
    'America/Juneau',
    'America/Boise',
    'America/Indiana/Indianapolis',
    'America/Kentucky/Louisville',
    'America/North_Dakota/Center',
    'America/Menominee',
    'Pacific/Honolulu',
    'America/Adak',
    'America/Nome',
    'America/Sitka',
    'America/Yakutat',
    'America/Metlakatla',
    'America/Indiana/Knox',
    'America/Indiana/Marengo',
    'America/Indiana/Petersburg',
    'America/Indiana/Vevay',
    'America/Indiana/Vincennes',
    'America/Indiana/Winamac',
    'America/Kentucky/Monticello'
  ];

  return usTimeZones.includes(timeZone);
};

/**
 * Format date based on user's geographic location
 * US users get MM/DD/YY format
 * Non-US users get DD/MM/YY format
 */
export const formatDateByLocale = (dateString: string): string => {
  const date = new Date(dateString);
  const isUS = isUSTimezone();

  // Get date parts
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear().toString().slice(-2); // Last 2 digits of year

  // Format based on geographic location
  return isUS ? `${month}/${day}/${year}` : `${day}/${month}/${year}`;
};

export const formatDateOnlyByLocale = (dateString: string): string => {
  const [year = '', month = '', day = ''] =
    isoToDateOnly(dateString).split('-');
  const isUS = isUSTimezone();
  const shortYear = year.slice(-2);

  return isUS ? `${month}/${day}/${shortYear}` : `${day}/${month}/${shortYear}`;
};

/**
 * Format date and time based on user's geographic location
 * US users get MM/DD/YYYY HH:mm format
 * Non-US users get DD/MM/YYYY HH:mm format
 */
export const formatDateTimeByLocale = (dateString: string): string => {
  const date = new Date(dateString);
  const isUS = isUSTimezone();

  // Get date parts
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');

  // Format based on geographic location
  const dateFormat = isUS
    ? `${month}/${day}/${year}`
    : `${day}/${month}/${year}`;

  return `${dateFormat} ${hours}:${minutes}`;
};

export const formatDateByUTC = (dateString: string): string => {
  const date = new Date(dateString);
  const isUS = isUSTimezone();

  const day = date.getUTCDate().toString().padStart(2, '0');
  const month = (date.getUTCMonth() + 1).toString().padStart(2, '0');
  const year = date.getUTCFullYear().toString().slice(-2);

  return isUS ? `${month}/${day}/${year}` : `${day}/${month}/${year}`;
};

export const isoToDateOnly = (
  iso: string,
  preserveDatePart: boolean = false
): string => {
  if (!iso) return '';
  if (preserveDatePart) {
    return iso.includes('T') ? iso.split('T')[0] : iso;
  }
  if (!iso.includes('T')) return iso;

  const date = new Date(iso);
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');

  return `${year}-${month}-${day}`;
};
